import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const SCROLL_KEYS = new Set([' ', 'Spacebar', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End']);

/**
 * Elements currently holding a page scroll lock.
 *
 * The previous lock did two things, and each one on its own broke
 * `position: sticky` across the whole page:
 *
 *   - `body { position: fixed; top: -scrollY }` — a fixed body has no scroll
 *     offset, so sticky children have nothing left to stick to.
 *   - `html[scroll-lock] { overflow: hidden }` — a root element that cannot
 *     scroll stops acting as a scrollport, so sticky resolves to its static
 *     position instead.
 *
 * Measured on a collection page: opening the cart drawer moved the filter
 * sidebar from a pinned `top: 120px` to `627 - scrollY`, i.e. entirely off
 * screen past roughly 1400px of scroll. The sticky header went with it.
 *
 * So we leave the document scrollable — sticky keeps working — and instead
 * refuse the gestures and keys that would scroll the page behind the dialog.
 * A scroll-position guard also catches browser scrollbar gestures, which can
 * bypass DOM wheel/touch handlers without changing the document's client width.
 * Anything aimed at a scrollable region inside the dialog still passes through.
 */
const scrollLocks = new Set();

let touchStartY = 0;
/** @type {{ x: number, y: number } | null} */
let lockedScrollPosition = null;

/** Restores page scrolling that bypassed the DOM gesture handlers. */
function handleLockedScroll() {
  const position = lockedScrollPosition;
  if (!position || (window.scrollX === position.x && window.scrollY === position.y)) return;

  // Numeric scrollTo works in older browsers, but follows root smooth-scroll
  // CSS. Override only that property for the synchronous restoration; neither
  // overflow nor document width changes, and authored inline priority survives.
  const style = document.documentElement.style;
  const behavior = style.getPropertyValue('scroll-behavior');
  const priority = style.getPropertyPriority('scroll-behavior');
  style.setProperty('scroll-behavior', 'auto', 'important');
  try {
    window.scrollTo(position.x, position.y);
  } finally {
    if (behavior) style.setProperty('scroll-behavior', behavior, priority);
    else style.removeProperty('scroll-behavior');
  }
}

/**
 * @param {TouchEvent} event
 */
function handleTouchStart(event) {
  touchStartY = event.touches[0]?.clientY ?? 0;
}

/**
 * @param {WheelEvent | TouchEvent} event
 * @returns {{ x: number, y: number } | null}
 */
function pointerPosition(event) {
  const touch = 'touches' in event ? event.touches[0] : null;
  if (touch) return { x: touch.clientX, y: touch.clientY };
  if ('clientX' in event) return { x: event.clientX, y: event.clientY };
  return null;
}

/**
 * How far the gesture wants to travel down the page. Positive scrolls towards
 * the end of the content.
 *
 * @param {WheelEvent | TouchEvent} event
 * @returns {number}
 */
function gestureDelta(event) {
  if ('deltaY' in event) return event.deltaY;
  const touch = 'touches' in event ? event.touches[0] : null;
  return touch ? touchStartY - touch.clientY : 0;
}

/**
 * Whether `node` is a scroll container with room left to move in `delta`'s
 * direction. A container already pinned at the relevant end would hand the
 * gesture on to the page, so it does not count as absorbing it.
 *
 * @param {HTMLElement} node
 * @param {number} delta
 * @returns {boolean}
 */
function canScroll(node, delta) {
  const { overflowY } = getComputedStyle(node);
  if (overflowY !== 'auto' && overflowY !== 'scroll') return false;

  const max = node.scrollHeight - node.clientHeight;
  if (max <= 1) return false;
  if (delta > 0) return node.scrollTop < max - 1;
  if (delta < 0) return node.scrollTop > 1;
  return false;
}

/**
 * Whether the gesture lands on something inside a locked element that can
 * actually consume it.
 *
 * Hit-testing the coordinates is what makes this work in both directions. The
 * event target is useless — a modal dialog makes the rest of the document
 * inert, so every wheel event names the dialog as its target even when the
 * pointer is over the page. And the dialog's bounding box is no good either:
 * on mobile the drawer covers the whole viewport, so a box test would call
 * every gesture "inside" and let the page scroll away behind it.
 *
 * @param {{ x: number, y: number }} point
 * @param {number} delta
 * @returns {boolean}
 */
function gestureIsAbsorbed(point, delta) {
  const hit = document.elementFromPoint(point.x, point.y);
  if (!hit) return false;

  let lock = null;
  for (const element of scrollLocks) {
    if (element.contains(hit)) {
      lock = element;
      break;
    }
  }
  if (!lock) return false;

  for (let node = hit; node instanceof HTMLElement; node = node.parentElement) {
    if (canScroll(node, delta)) return true;
    if (node === lock) break;
  }

  return false;
}

/**
 * Blocks wheel and touch gestures that would scroll the page behind a locked
 * element, while letting scrollable regions inside it work normally.
 *
 * @param {WheelEvent | TouchEvent} event
 */
function handleLockedGesture(event) {
  if (!scrollLocks.size) return;

  // Leave horizontal gestures alone — carousels inside the dialog need them.
  if ('deltaX' in event && Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

  const point = pointerPosition(event);
  if (point && gestureIsAbsorbed(point, gestureDelta(event))) return;

  event.preventDefault();
}

/**
 * Blocks the keys that scroll the document, unless the keystroke belongs to a
 * text field or to a scrollable region inside the locked element.
 *
 * @param {KeyboardEvent} event
 */
function handleLockedKeydown(event) {
  if (!scrollLocks.size || !SCROLL_KEYS.has(event.key)) return;

  const active = document.activeElement;

  if (
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement ||
    active instanceof HTMLSelectElement ||
    (active instanceof HTMLElement && active.isContentEditable)
  ) {
    return;
  }

  // Space activates buttons, summaries and ARIA widgets; only swallow it on the page itself
  // or on non-interactive elements, where it would scroll the locked page.
  if (
    (event.key === ' ' || event.key === 'Spacebar') &&
    active instanceof HTMLElement &&
    active.matches(
      'button, summary, [role="button"], [role="checkbox"], [role="switch"], [role="radio"], [role="tab"], [role="menuitem"], [role="option"]'
    )
  ) {
    return;
  }

  for (let node = active; node instanceof HTMLElement; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (node.scrollHeight > node.clientHeight + 1 && (overflowY === 'auto' || overflowY === 'scroll')) return;
  }

  event.preventDefault();
}

/**
 * Locks page scrolling while `element` is open, leaving the document's scroll
 * offset and every `position: sticky` element untouched.
 *
 * @param {HTMLElement} element
 */
export function lockScroll(element) {
  if (scrollLocks.has(element)) return;

  if (!scrollLocks.size) {
    lockedScrollPosition = { x: window.scrollX, y: window.scrollY };
    window.addEventListener('scroll', handleLockedScroll, { passive: true });
    document.addEventListener('wheel', handleLockedGesture, { passive: false, capture: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', handleLockedGesture, { passive: false, capture: true });
    document.addEventListener('keydown', handleLockedKeydown, { capture: true });
  }

  scrollLocks.add(element);
}

/**
 * Releases the scroll lock held by `element`.
 *
 * @param {HTMLElement} element
 */
export function unlockScroll(element) {
  if (!scrollLocks.delete(element) || scrollLocks.size) return;

  window.removeEventListener('scroll', handleLockedScroll);
  lockedScrollPosition = null;
  document.removeEventListener('wheel', handleLockedGesture, { capture: true });
  document.removeEventListener('touchstart', handleTouchStart, { capture: true });
  document.removeEventListener('touchmove', handleLockedGesture, { capture: true });
  document.removeEventListener('keydown', handleLockedKeydown, { capture: true });
}


/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #dialogGeneration = 0;
  #listenerTimer;

  #handleNativeClose = () => {
    // A queued close event from an earlier opening must not unlock a reopen.
    if (this.refs.dialog.open) return;
    this.#dialogGeneration++;
    clearTimeout(this.#listenerTimer);
    unlockScroll(this.refs.dialog);
    this.refs.dialog.classList.remove('dialog-closing');
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  };

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('close', this.#handleNativeClose);
    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    // Theme-editor section moves can detach an open modal. Release its exact
    // lock immediately, without waiting for an animation on a detached node.
    this.#dialogGeneration++;
    clearTimeout(this.#listenerTimer);
    const dialog = this.refs.dialog;
    if (dialog) {
      dialog.removeEventListener('close', this.#handleNativeClose);
      if (dialog.open) dialog.close();
      unlockScroll(dialog);
      dialog.classList.remove('dialog-closing');
    }
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#handleResize.cancel();
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const generation = ++this.#dialogGeneration;
    dialog.showModal();
    lockScroll(dialog);
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    this.#listenerTimer = setTimeout(() => {
      if (!this.isConnected || !dialog.open || generation !== this.#dialogGeneration) return;
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || dialog.classList.contains('dialog-closing')) return;
    const generation = ++this.#dialogGeneration;
    clearTimeout(this.#listenerTimer);

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (!this.isConnected || generation !== this.#dialogGeneration) return;
    dialog.close();
    unlockScroll(dialog);
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        if (event.target.open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
